import React, { useCallback, useEffect, useState, useRef } from "react";
import "./ConnectWallet.css";
import { userWalletAddress, getDailyRewardsConfig, claimDailyReward } from "../../apis/user";
import useUserInfo from "../../Hooks/useUserInfo";
import { ethers, BrowserProvider, formatEther } from "ethers";
import Spinner from "../Streak/Spinner";
import Swal from "sweetalert2";
import contractABI from "./contractABI.js";
import ConnectWalletButton from "./ConnectWalletButton.js";
import {useDisconnect, useAppKitNetwork, useAppKitProvider } from "@reown/appkit/react";


// Contract ABI - including the functions we need
// const contractABI = contractABI;

// Function to get the appropriate contract address based on network
const getContractAddress = () => {
  // Get network from environment variable
  const network = process.env.REACT_APP_NETWORK || 'testnet';
  
  // Select contract address based on network
  if (network === 'testnet') {
    const testnetAddress = process.env.REACT_APP_TESTNET_CONTRACT_ADDRESS;
    return testnetAddress;
  } else {
    const mainnetAddress = process.env.REACT_APP_MAINNET_CONTRACT_ADDRESS;
    return mainnetAddress;
  }
};

// Get the contract address based on environment
const CONTRACT_ADDRESS = getContractAddress();

// Validate that contract address is available
if (!CONTRACT_ADDRESS) {
  console.error("No contract address found for the current network!");
}

export default function ConnectWalletPage() {
  const [signer, setSigner] = useState(null);
  const [address, setAddress] = useState(null);
  const [balance, setBalance] = useState(null);
  const [error, setError] = useState(null);
  const [contract, setContract] = useState(null);

  const { disconnect } = useDisconnect();
  const { walletProvider } = useAppKitProvider('eip155');
  const {caipNetwork } = useAppKitNetwork();

  useEffect(() => {
    const fetchWalletData = async () => {
      try {
        if (walletProvider) {
          const ethersProvider = new BrowserProvider(walletProvider);
          const _signer = await ethersProvider.getSigner();
          const userAddress = await _signer.getAddress();
          const userBalance = await ethersProvider.getBalance(userAddress);

          setSigner(_signer);
          setAddress(userAddress);
          setBalance(formatEther(userBalance));

          // Set up contract with the new signer
          const _contract = new ethers.Contract(CONTRACT_ADDRESS, contractABI, signer);
          setContract(_contract);
        } else {
          setAddress(null);
          setSigner(null);
          setBalance(null);
          setContract(null);
        }
      } catch (err) {
        setError(err.message);
        console.error('Error fetching wallet data:', err);
      }
    };

    fetchWalletData();
  }, [walletProvider]);
  const [isClaiming, setIsClaiming] = useState(false);


  const [isLoading, setIsLoading] = useState(false);
  const [claimStatus, setClaimStatus] = useState({});
  const [dailyRewardsConfig, setDailyRewardsConfig] = useState([]);
  const [isLoadingRewards, setIsLoadingRewards] = useState(true);
  const [currentWithdrawalDay, setCurrentWithdrawalDay] = useState(null);
  const [loadingDay, setLoadingDay] = useState(null);
  const [originalTokenAmounts, setOriginalTokenAmounts] = useState({});
  const [copied, setCopied] = useState(false);
  const [walletCooldown, setWalletCooldown] = useState(null);
  const [cooldownTimer, setCooldownTimer] = useState(null);
  const isInitialLoad = useRef(true);
  const hasFetchedConfig = useRef(false);
  const isFetchingConfig = useRef(false);

  const { userDetails } = useUserInfo();

  // Timer effect for wallet cooldown
  useEffect(() => {
    if (walletCooldown && walletCooldown.isInCooldown) {
      console.log("Starting cooldown timer for:", walletCooldown.cooldownEndsAt);
      const interval = setInterval(() => {
        const now = new Date().getTime();
        const cooldownEnd = new Date(walletCooldown.cooldownEndsAt).getTime();
        const timeRemaining = cooldownEnd - now;

        if (timeRemaining <= 0) {
          // Cooldown has ended
          console.log("Cooldown timer ended");
          setWalletCooldown(prev => prev ? { ...prev, isInCooldown: false } : null);
          setCooldownTimer(null);
          clearInterval(interval);
        } else {
          // Update timer
          const hours = Math.floor(timeRemaining / (1000 * 60 * 60));
          const minutes = Math.floor((timeRemaining % (1000 * 60 * 60)) / (1000 * 60));
          const seconds = Math.floor((timeRemaining % (1000 * 60)) / 1000);
          
          setCooldownTimer({
            hours,
            minutes,
            seconds,
            totalSeconds: Math.floor(timeRemaining / 1000)
          });
        }
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [walletCooldown]);

  // Fetch daily rewards configuration from API
  const fetchDailyRewardsConfig = async (forceRefresh = false) => {
    // Prevent duplicate API calls unless forced refresh
    if (!forceRefresh && hasFetchedConfig.current && dailyRewardsConfig.length > 0) {
      console.log("Daily rewards config already fetched, skipping API call");
      return;
    }
    
    // Prevent multiple simultaneous API calls
    if (isFetchingConfig.current) {
      console.log("Already fetching daily rewards config, skipping duplicate call");
      return;
    }
    
    console.log("🔄 Fetching daily rewards config...");
    
    try {
      setIsLoadingRewards(true);
      hasFetchedConfig.current = true;
      isFetchingConfig.current = true;
      
      // Get telegram ID from userDetails
      const telegramId = userDetails?.userDetails?.telegramId;
      
      if (!telegramId) {
        // Fallback to default values if no telegram ID
        setDailyRewardsConfig([
          { day: 1, tokens: 0, date: "NOT PLAYED" },
          { day: 2, tokens: 0, date: "NOT PLAYED" },
          { day: 3, tokens: 0, date: "NOT PLAYED" },
          { day: 4, tokens: 0, date: "NOT PLAYED" },
          { day: 5, tokens: 0, date: "NOT PLAYED" },
          { day: 6, tokens: 0, date: "NOT PLAYED" },
          { day: 7, tokens: 0, date: "NOT PLAYED" }
        ]);
        return;
      }
      
      const response = await getDailyRewardsConfig(telegramId);
      
      if (response && response.success) {
        // Use the data from API response
        if (response.data) {
          
          // Convert the Day 1, Day 2, etc. format to array format
          var dailyRewardsArray = [];
          var claimStatusObj = {};
          
          // Get all day keys from the response and find the maximum day
          const dayKeys = Object.keys(response.data.data).filter(key => key.startsWith('Day '));
          
          let maxDay = 7; // Default fallback
          if (dayKeys.length > 0) {
            maxDay = Math.max(...dayKeys.map(key => parseInt(key.replace('Day ', ''))));
          }
          
          
          // Process each day from the response
          for (let i = 1; i <= maxDay; i++) {
            
            const dayKey = `Day ${i}`;
            const dayData = response.data.data[dayKey];
            
            if (dayData) {
              const tokenAmount = dayData.tokens || 0; // Use 0 as default instead of 1000
              
              // Debug logging for staking info
              
              // Store original token amount for this day
              setOriginalTokenAmounts(prev => ({
                ...prev,
                [i]: tokenAmount
              }));
              
              dailyRewardsArray.push({
                day: i,
                tokens: tokenAmount,
                claimed: dayData.claimed || false,
                claimable: dayData.claimable || false,
                date: dayData.date,
                userStaking: dayData.userStaking || false,
                stakingInfo: dayData.stakingInfo || null
              });
              
              // Set claim status
              claimStatusObj[`day-${i}`] = dayData.claimed || false;
            } else {
              // For missing days, check if they were previously claimed
              const previouslyClaimed = claimStatusObj[`day-${i}`] || false;
              const originalAmount = originalTokenAmounts[i] || 0;
              
              // If previously claimed, preserve the original token amount
              // If not claimed, show as not played
              dailyRewardsArray.push({
                day: i,
                tokens: previouslyClaimed ? originalAmount : 0, // Preserve original token amount for claimed days
                claimed: previouslyClaimed,
                claimable: false,
                date: previouslyClaimed ? "CLAIMED" : "NOT PLAYED",
                userStaking: false,
                stakingInfo: null
              });
              claimStatusObj[`day-${i}`] = previouslyClaimed;
            }
          }
          
          setDailyRewardsConfig(dailyRewardsArray);
          setClaimStatus(claimStatusObj);
          
          // Check for currentWithdrawalDay in userInfo object
          if (response.data.userInfo && response.data.userInfo.currentWithdrawalDay) {
            setCurrentWithdrawalDay(response.data.userInfo.currentWithdrawalDay);
          } else if (response.data.currentWithdrawalDay) {
            setCurrentWithdrawalDay(response.data.currentWithdrawalDay);
          } else if (response.data.current_withdrawal_day) {
            setCurrentWithdrawalDay(response.data.current_withdrawal_day);
          } else if (response.data.currentDay) {
            setCurrentWithdrawalDay(response.data.currentDay);
          } else {
            // Fallback: find the next available day to claim
            const nextAvailableDay = dailyRewardsArray.find(day => day.claimable && !day.claimed);
            if (nextAvailableDay) {
              setCurrentWithdrawalDay(nextAvailableDay.day);
            }
          }
          
          // Store merkle proof data for later use in claiming
          if (response.data.merkleProof) {
            // You can store this in state if needed for claiming
            localStorage.setItem('merkleProofData', JSON.stringify(response.data.merkleProof));
          }
          
          // Handle wallet cooldown data
          if (response.data.walletCooldown) {
            console.log("Wallet cooldown data received:", response.data.walletCooldown);
            setWalletCooldown(response.data.walletCooldown);
          }
        }
      } else {
        // Fallback to default values if API fails
        setDailyRewardsConfig([
          { day: 1, tokens: 0, date: "NOT PLAYED" },
          { day: 2, tokens: 0, date: "NOT PLAYED" },
          { day: 3, tokens: 0, date: "NOT PLAYED" },
          { day: 4, tokens: 0, date: "NOT PLAYED" },
          { day: 5, tokens: 0, date: "NOT PLAYED" },
          { day: 6, tokens: 0, date: "NOT PLAYED" },
          { day: 7, tokens: 0, date: "NOT PLAYED" }
        ]);
      }
    } catch (error) {
      // Fallback to default values if API fails
      setDailyRewardsConfig([
        { day: 1, tokens: 0, date: "NOT PLAYED" },
        { day: 2, tokens: 0, date: "NOT PLAYED" },
        { day: 3, tokens: 0, date: "NOT PLAYED" },
        { day: 4, tokens: 0, date: "NOT PLAYED" },
        { day: 5, tokens: 0, date: "NOT PLAYED" },
        { day: 6, tokens: 0, date: "NOT PLAYED" },
        { day: 7, tokens: 0, date: "NOT PLAYED" }
      ]);
    } finally {
      setIsLoadingRewards(false);
      isFetchingConfig.current = false; // Reset the flag after fetch
    }
  };

  // Generate claim buttons based on API data
  const generateClaimDays = () => {
    const days = [];
    
    // Use API data if available
    const rewardsData = dailyRewardsConfig;
    // Use all days from the API response
    rewardsData.forEach(rewardData => {
      days.push({
        day: rewardData.day,
        id: `day-${rewardData.day}`,
        claimed: rewardData.claimed || false,
        disabled: !rewardData.claimable && !rewardData.claimed, // Disable if not claimable and not claimed
        claimable: rewardData.claimable || false,
        tokens: rewardData.tokens || 0,
        date: rewardData.date || "NOT PLAYED",
        userStaking: rewardData.userStaking || false,
        stakingInfo: rewardData.stakingInfo || null
      });
    });
    return days;
  };

  const handleClaim = async (dayId) => {
    try {
      setLoadingDay(dayId);
      
      // Check if already claiming
      if (isClaiming) {
        return;
      }
      
      // Get telegram ID from userDetails with better debugging
      const telegramId = userDetails?.userDetails?.telegramId;
       
      if (!telegramId) {
        console.error("Telegram ID is missing from userDetails:", userDetails);
        Swal.fire({
          title: "Login Required",
          text: "Telegram ID not found. Please make sure you are logged in with your Telegram account.",
          icon: "warning",
          confirmButtonText: "Okay",
        });
        return;
      }
      
      // Check if wallet is connected
      if (!address) {
        console.error('Wallet not connected');
        Swal.fire({
          title: "Wallet Required",
          text: "Please connect your wallet first",
          icon: "warning",
          confirmButtonText: "Okay",
        });
        return;
      }
      
      // Extract day number from dayId (e.g., "day-12" -> 12)
      const dayNumber = parseInt(dayId.replace('day-', ''));
      
      // STEP 1: Get merkle proof data from localStorage (from getDailyRewardsConfig API)
      const merkleProofData = localStorage.getItem('merkleProofData');
      
      if (!merkleProofData) {
        console.error('No merkle proof data found. Please refresh the page.');
        Swal.fire({
          title: "Data Error",
          text: "No claim data found. Please refresh the page and try again.",
          icon: "error",
          confirmButtonText: "Okay",
        });
        return;
      }
      
      const merkleProof = JSON.parse(merkleProofData);
      
      // Extract proof, index, and amount from merkle proof data
      // Based on the localStorage structure: { currentClaimableDay: 14, proofData: { proof, index, amount, address, root } }
      const { proofData } = merkleProof;
      const { proof, index, amount, address: merkleAddress, root } = proofData;
      console.log("merkleProofData", merkleProofData);
      
      
      if (!proof || index === undefined || index === null || amount === undefined || amount === null) {
        console.error('Missing claim data from merkle proof');
        Swal.fire({
          title: "Claim Error",
          text: "Unable to get claim data. Please refresh the page and try again.",
          icon: "error",
          confirmButtonText: "Okay",
        });
        return;
      }
      
      // STEP 2: Execute blockchain transaction
              // Check if contract is available
        if (!contract) {
          console.error('Contract not available');
          Swal.fire({
            title: "Contract Error",
            text: "Smart contract not available. Please try again.",
            icon: "error",
            confirmButtonText: "Okay",
          });
          return;
        }
        
        // Validate contract address format
        if (!CONTRACT_ADDRESS || !CONTRACT_ADDRESS.startsWith('0x') || CONTRACT_ADDRESS.length !== 42) {
          console.error('Invalid contract address format:', CONTRACT_ADDRESS);
          Swal.fire({
            title: "Contract Error",
            text: "Invalid contract address format. Please check your environment variables.",
            icon: "error",
            confirmButtonText: "Okay",
          });
          return;
        }
    
              // Test contract connection by calling a view function
        try {
          // First, let's try to get the contract code to see if it's deployed
          try {
            // Use thirdweb's built-in method to check contract deployment
            const root = await contract.merkleRoot();
            console.log("Contract is deployed and accessible");
          } catch (codeError) {
            console.error("Error checking contract deployment:", codeError);
            Swal.fire({
              title: "Contract Not Deployed",
              text: `No contract found at address ${CONTRACT_ADDRESS} on ${caipNetwork?.name}. Please check if the contract is deployed on the correct network.`,
              icon: "error",
              confirmButtonText: "Okay",
            });
            return;
          }
          
          const root = await contract.merkleRoot();
          console.log("merkleRoot", root);
          
        } catch (rootError) {
          console.error("Error calling merkleRoot:", rootError);
          console.error("This suggests the contract address might be invalid or the contract is not deployed");
          
          // Show error to user
          Swal.fire({
            title: "Contract Error",
            text: "Unable to connect to the smart contract. Please check if the contract is deployed and the address is correct.",
            icon: "error",
            confirmButtonText: "Okay",
          });
          return;
        }
      
      
      // Validate proof format - proof is already an array
      const proofArray = Array.isArray(proof) ? proof : [proof];
      console.log("proof of Array://", proofArray);
      
      
      // Validate each proof element
      for (const p of proofArray) {
        if (!p.startsWith('0x') || p.length !== 66) {
          throw new Error('Invalid proof format. Each proof element should be a 32-byte hex string starting with 0x');
        }
      }

      // Validate index is a positive number
      const indexNum = parseInt(index);
      if (isNaN(indexNum) || indexNum < 0) {
        throw new Error('Index must be a positive number');
      }

      // Validate amount is a positive number
      const amountNum = parseFloat(amount);
      if (isNaN(amountNum)) {
        throw new Error('Amount must be a valid number');
      }
      
      if (amountNum === 0) {
        Swal.fire({
          title: "No Tokens Available",
          text: "There are no tokens to claim for this day.",
          icon: "info",
          confirmButtonText: "Okay",
        });
        return;
      }
      
      if (amountNum < 0) {
        throw new Error('Amount must be a positive number');
      }

      // Use amount directly without converting to wei
      const amountToSend = amountNum;

      console.log('Blockchain Claim Parameters:', {
        proof: proofArray,
        index: indexNum,
        amount: amountToSend,
        contractAddress: CONTRACT_ADDRESS,
        account: address,
        dayId: dayId
      });

      console.log('About to call claimDistributionAirDrop using thirdweb...');
      

      // Call the claimDistributionAirDrop function
      const tx = await contract.claimDistributionAirDrop(
        proofArray,
        indexNum,
        amountToSend,
        {
          gasLimit: 500000 // Add explicit gas limit
        }
      );
      
      // // Use thirdweb's contract write method
      // const tx = await claimDistributionAirDrop({
      //   args: [proofArray, indexNum, amountToSend],
      //   overrides: {
      //     gasLimit: 500000
      //   }
      // });

      console.log('Blockchain transaction sent:', tx);
      console.log('Transaction hash:', tx.receipt?.transactionHash || tx.hash);

      // Wait for transaction to be mined and confirmed
      const receipt = await tx.wait();
      console.log('Blockchain transaction receipt:', receipt);
      
      // Check if transaction was successful
      if (receipt.status === 1) {
        console.log("Blockchain transaction successful, calling backend API for confirmation...");
        
        // STEP 3: Only call backend API after successful blockchain transaction
        const claimData = {
          telegramId: String(telegramId),
          day: dayNumber
        };
        const confirmResponse = await claimDailyReward(claimData);
        console.log("Backend confirmation response:", confirmResponse);
        
        if (confirmResponse && confirmResponse.success) {
          // Update local state to reflect the claimed status
          setClaimStatus(prev => ({
            ...prev,
            [dayId]: true
          }));
          
          // Show success message
          const transactionHash = tx.receipt?.transactionHash || tx.hash;
          Swal.fire({
            title: "Success!",
            text: `Daily reward claimed successfully! Transaction hash: ${transactionHash}`,
            icon: "success",
            confirmButtonText: "Okay",
          });
          
          // Refresh the daily rewards data
          setTimeout(() => {
            refreshDailyRewardsConfig();
          }, 1000);
          
        } else {
          console.error("Backend confirmation failed:", confirmResponse?.error || confirmResponse?.message);
          Swal.fire({
            title: "Warning",
            text: "Blockchain transaction successful but backend confirmation failed. Please contact support.",
            icon: "warning",
            confirmButtonText: "Okay",
          });
        }
      } else {
        console.error("Blockchain transaction failed");
        Swal.fire({
          title: "Transaction Failed",
          text: "The blockchain transaction failed. Please try again.",
          icon: "error",
          confirmButtonText: "Okay",
        });
      }
      
    } catch (error) {
      console.error('=== ERROR DETAILS ===');
      console.error('Error message:', error.message);
      console.error('Error code:', error.code);
      console.error('Error data:', error.data);
      console.error('Error transaction:', error.transaction);
      console.error('Error receipt:', error.receipt);
      console.error('Transaction status:', error.receipt?.status);
      console.error('Gas used:', error.receipt?.gasUsed?.toString());
      console.error('Transaction hash:', error.receipt?.transactionHash);
      console.error('Full error object:', error);
      console.error('Error stack:', error.stack);
      console.error('=== END ERROR DETAILS ===');

      let errorMessage = 'Failed to claim daily reward';
      
      // Check if transaction was reverted
      if (error.receipt && error.receipt.status === 0) {
        errorMessage = 'Transaction was reverted. This could be due to:';
        
        // Try to decode the revert reason if available
        if (error.data) {
          try {
            const contract = new ethers.Contract(CONTRACT_ADDRESS, contractABI);
            const decodedError = contract.interface.parseError(error.data);
            errorMessage = `Contract error: ${decodedError.name}`;
          } catch (e) {
            console.error('Error decoding contract error:', e);
            errorMessage = 'Transaction reverted - check if you have already claimed this reward or if the proof is valid';
          }
        } else {
          errorMessage = 'Transaction reverted - check if you have already claimed this reward or if the proof is valid';
        }
      } else if (error.message.includes('user rejected')) {
        errorMessage = 'Transaction was rejected by user';
      } else if (error.message.includes('insufficient funds')) {
        errorMessage = 'Insufficient funds for gas';
      } else if (error.message.includes('Already claimed')) {
        errorMessage = 'This daily reward has already been claimed';
      } else if (error.message.includes('Invalid proof')) {
        errorMessage = 'Invalid merkle proof provided';
      } else if (error.message.includes('Distribution cap exceeded')) {
        errorMessage = 'Distribution cap has been reached';
      } else if (error.message.includes('Invalid proof format')) {
        errorMessage = error.message;
      } else {
        errorMessage = `Transaction failed: ${error.message}`;
      }

      Swal.fire({
        title: "Claim Error",
        text: `Error claiming daily reward: ${errorMessage}`,
        icon: "error",
        confirmButtonText: "Okay",
      });
    } finally {
      setLoadingDay(null);
    }
  };

  // New function to handle claiming the current withdrawal day
  const handleClaimAll = async () => {
    // Check for Telegram ID first
    const telegramId = userDetails?.userDetails?.telegramId;
    if (!telegramId) {
      console.error("Telegram ID is missing in handleClaimAll:", userDetails);
      Swal.fire({
        title: "Login Required",
        text: "Telegram ID not found. Please make sure you are logged in with your Telegram account.",
        icon: "warning",
        confirmButtonText: "Okay",
      });
      return;
    }
    
    // Check for wallet cooldown
    if (walletCooldown && walletCooldown.isInCooldown) {
      Swal.fire({
        title: "Wallet in Cooldown",
        text: `Your wallet is currently in cooldown. Please wait until the timer expires before claiming again.`,
        icon: "warning",
        confirmButtonText: "Okay",
      });
      return;
    }
    
    if (!currentWithdrawalDay) {
      Swal.fire({
        title: "No Day Available",
        text: "No current withdrawal day available!",
        icon: "warning",
        confirmButtonText: "Okay",
      });
      return;
    }
    
    const dayId = `day-${currentWithdrawalDay}`;
    await handleClaim(dayId);
  };

  // Function to check if there are unclaimed days before or including currentWithdrawalDay
  const hasCumulativeTokensAvailable = () => {
    if (!currentWithdrawalDay) return false;
    
    // If wallet is in cooldown, return false
    if (walletCooldown && walletCooldown.isInCooldown) {
      return false;
    }
    
    const days = generateClaimDays();
    const previousDays = days.filter(day => day.day <= currentWithdrawalDay);
  
    
    // Filter out days that are not played (exclude "NOT PLAYED" days)
    const playedDays = previousDays.filter(day => day.date !== "NOT PLAYED");
    

    
    // If no days have been played, return false
    if (playedDays.length === 0) {
      return false;
    }
    
    // Check if all played days up to current withdrawal day are claimed
    const allPlayedDaysClaimed = playedDays.every(day => day.claimed);
    
    
    // Return true if at least one played day is not claimed (meaning there are unclaimed rewards)
    // Return false if all played days are claimed (meaning no rewards available)
    const hasUnclaimedDays = !allPlayedDaysClaimed;
    
    return hasUnclaimedDays;
  };

  // Function to check if a day is the current withdrawal day
  const isCurrentWithdrawalDay = (dayNumber) => {
    // Convert both values to numbers for comparison
    const dayNum = Number(dayNumber);
    const currentDay = Number(currentWithdrawalDay);
    
    const isCurrent = dayNum === currentDay;
    return isCurrent;
  };

  const handleWalletConnection = useCallback(async (walletAddress) => {
    setIsLoading(false);
    
    // Show success message
    Swal.fire({
      title: "Wallet Connected!",
      text: `Successfully connected wallet: ${walletAddress.slice(0, 6)}...${walletAddress.slice(-4)}`,
      icon: "success",
      confirmButtonText: "Great!",
      timer: 3000,
      timerProgressBar: true,
    });
    

    
    if (userDetails?.userDetails?.telegramId) {
      console.log("Wallet connected, fetching daily rewards config...");
      await fetchDailyRewardsConfig(true); // Force refresh after wallet connection
    } else {
      console.log("No telegram ID available, skipping daily rewards config fetch");
    }
  }, [userDetails?.userDetails?.telegramId]);

  const handleWalletDisconnection = useCallback(() => {
    setIsLoading(false);
  }, []);

  const updateWalletAddress = async (walletAddressString) => {
 
    
    const data = {
      userWalletAddress: walletAddressString,
    };
    // update wallet address
    const response = await userWalletAddress(data);
    // Fetch daily rewards config if we have a telegram ID and this is a wallet connection (not disconnection)
    if (userDetails?.userDetails?.telegramId && walletAddressString) {
      console.log("Wallet address updated, fetching daily rewards config...");
      await fetchDailyRewardsConfig(true); // Force refresh after wallet connection
    } else {
      console.log("Skipping daily rewards config fetch - no telegram ID or empty wallet address");
    }
  };

  useEffect(() => {
    if (address) {
      handleWalletConnection(address);
    } else {
      handleWalletDisconnection();
    }
  }, [address, handleWalletConnection, handleWalletDisconnection]);

  // Call API only when user connects wallet (address changes from null to valid)
  useEffect(() => {
    if (isInitialLoad.current) {
      // Skip API call on initial load
      isInitialLoad.current = false;
      return;
    }
    
    if (address) {
      // Only call API when address is valid (user connected) and not on initial load
      updateWalletAddress(address);
    }
  }, [address]); // This will run when address changes

  // Call API only once when component mounts
  useEffect(() => {
    // Only fetch on mount if we have a telegram ID
    if (userDetails?.userDetails?.telegramId) {
      fetchDailyRewardsConfig();
    }
  }, []); // Empty dependency array means this runs once on mount

  // Show wallet connection prompt on first load if no wallet is connected
  useEffect(() => {
    if (!address && !isInitialLoad.current) {
      Swal.fire({
        title: "Wallet Required",
        text: "No wallet address detected. Connect your wallet to proceed.",
        icon: "info",
        confirmButtonText: "Connect Wallet",
        showCancelButton: false,
        allowOutsideClick: true,
        allowEscapeKey: true,
      });
    }
  }, [address]); // This will run when address changes

  // Debug userDetails changes and refetch config when telegram ID becomes available
  useEffect(() => {
    // Only refetch if we have a telegram ID and we're not already loading
    // This prevents unnecessary API calls on every userDetails change
    if (userDetails?.userDetails?.telegramId && !isLoadingRewards && !dailyRewardsConfig.length) {
      console.log("Telegram ID available, fetching daily rewards config");
      fetchDailyRewardsConfig();
    }
  }, [userDetails?.userDetails?.telegramId]); // Only depend on telegramId, not entire userDetails object

  const handleDisconnect = async () => {
    setIsLoading(true);
    disconnect();
    // Call API when user explicitly disconnects
    await updateWalletAddress("");
    setIsLoading(false);
  };

  const formatAddress = (address) => {
    if (!address) return "";
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  // Function to format token amounts (e.g., 10000 -> 10k)
  const formatTokenAmount = (amount) => {
    if (amount >= 1000) {
      return `${(amount / 1000).toFixed(amount % 1000 === 0 ? 0 : 1)}k`;
    }
    return amount.toString();
  };

  // Function to copy wallet address to clipboard
  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000); // Reset after 2 seconds
    } catch (err) {
      console.error('Failed to copy: ', err);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000); // Reset after 2 seconds
    }
  };

  // Timer component for cooldown display
  const CooldownTimer = ({ timer }) => {
    if (!timer) return null;
    
    const formatTime = (value) => value.toString().padStart(2, '0');
    
    return (
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        gap: '8px',
        fontSize: '14px',
        fontWeight: 'bold'
      }}>
        <span>⏰ Cooldown:</span>
        <span style={{ color: '#ff6b6b' }}>
          {formatTime(timer.hours)}:{formatTime(timer.minutes)}:{formatTime(timer.seconds)}
        </span>
      </div>
    );
  };

  // Function to manually refresh daily rewards config
  const refreshDailyRewardsConfig = async () => {
    hasFetchedConfig.current = false; // Reset the flag to allow fresh fetch
    isFetchingConfig.current = false; // Reset the fetching flag
    await fetchDailyRewardsConfig();
  };

  if (isLoadingRewards || isLoading) {
    return (
      <main className="container">
        <Spinner />
      </main>
    );
  }

  return (
    <React.Fragment>
      <main className="container">
        {address ? (
          <div>
            <button
              className="connectWallet-button"
              onClick={handleDisconnect}
            >
              Disconnect Wallet
            </button>
            <p className="wallet-address">
              Connected: {formatAddress(address)}
              <button 
                className="btn-copy"
                onClick={() => copyToClipboard(address)}
                title="Copy full address"
              >
                <svg
                  className="button-icon"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M16 1H4C2.9 1 2 1.9 2 3v14h2V3h12V1zm3 4H8C6.9 5 6 5.9 6 7v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z" />
                </svg>
                {copied ? "COPIED!" : "COPY"}
              </button>
            </p>
          </div>
        ) : (
          <div>
            <ConnectWalletButton />
          </div>
        )}
        
        {/* Day-wise Claim Section */}
        <div className="connect-wallet-claim-section">
          <h3 
            className="connect-wallet-claim-section-title"
            style={{
              textAlign: 'center',
              background: 'linear-gradient(180deg, #fd8d23 27.59%, #fae61c 32.74%, #eef73b 37.45%, #4cf038 46.86%, #33c6f6 56.95%, #7475ff 63.9%, #d457ff 72.41%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              fontFamily: 'mySecondFont',
              fontSize: '16px',
              fontWeight: 'bold'
            }}
          >
            Daily Rewards
          </h3>
          
          {/* Telegram Login Status */}
          {!userDetails?.userDetails?.telegramId && (
            <div className="connect-wallet-telegram-warning">
              <p>⚠️ Please log in with your Telegram account to claim daily rewards</p>
            </div>
          )}
          <div className="connect-wallet-claim-buttons-container">
            {generateClaimDays().map((day) => (
              <div
                key={day.id}
                className={`connect-wallet-day-item ${day.claimed ? 'claimed' : ''} ${day.disabled ? 'disabled' : ''} ${isCurrentWithdrawalDay(day.day) ? 'current-day' : ''} ${day.claimable && !day.claimed ? 'clickable' : ''}`}
                onClick={() => {
                  // Remove API call - just for display purposes
                }}
                style={{ cursor: day.claimable && !day.claimed ? 'pointer' : 'default' }}
              >
                <div className="connect-wallet-day-left">
                  <span className="connect-wallet-day-number">DAY {day.day}</span>
                  {day.date && (
                    <span className={`connect-wallet-day-date ${day.date === "NOT PLAYED" ? 'not-played' : ''}`}>
                      {day.date}
                    </span>
                  )}
                </div>
                <div className="connect-wallet-day-item-right">
                  <div className="connect-wallet-token-amount-container">
                    <span className="connect-wallet-token-amount">{formatTokenAmount(day.tokens)}</span>
                    <span className="connect-wallet-token-label">TOKENS</span>
                  </div>
                  <span className="connect-wallet-status-icon">
                    {loadingDay === day.id ? (
                      <Spinner />
                    ) : day.claimed ? (
                      '✓' // Tick symbol for claimed days
                    ) : day.userStaking ? (
                      day.stakingInfo?.canClaimAfterLockPeriod ? (
                        <span>🔓</span> //unlock if can claim after lock period
                      ) : (
                        '🔒' // Lock if staked but cannot claim yet
                      )
                    ) : (
                      <span>🔓</span> //unlock for unstaked days
                    )}
                  </span>
                </div>
              </div>
            ))}
          </div>
          <button
            className="connect-wallet-claim-all-button"
            onClick={handleClaimAll}
            disabled={loadingDay || isClaiming || !currentWithdrawalDay || !userDetails?.userDetails?.telegramId || !hasCumulativeTokensAvailable() || (walletCooldown && walletCooldown.isInCooldown)}
          >
            {loadingDay === `day-${currentWithdrawalDay}` || isClaiming ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                <Spinner />
                <span>Claiming...</span>
              </div>
            ) : walletCooldown && walletCooldown.isInCooldown ? (
              <CooldownTimer timer={cooldownTimer} />
            ) : !userDetails?.userDetails?.telegramId ? 'Login Required' :
             !hasCumulativeTokensAvailable() ? 'No Tokens Available' :
             currentWithdrawalDay ? `Claim` : 'No CLAIM Available'}
          </button>
        </div>
      </main>
    </React.Fragment>
  );
}
